// Durable log storage backed by IndexedDB with a 30-minute retention window and a hard
// entry cap. Falls back to an in-memory array when IndexedDB is unavailable so the
// app never breaks. All errors are swallowed — logging must never disrupt product code.
import type { LogEntry } from './types';

const DB_NAME = 'songhunt-logs';
const STORE = 'entries';
const DB_VERSION = 1;
const RETENTION_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 5000;

let dbPromise: Promise<IDBDatabase | null> | null = null;
let useMemory = false;
const memoryFallback: LogEntry[] = [];

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        useMemory = true;
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
          store.createIndex('ts', 'ts');
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        useMemory = true;
        resolve(null);
      };
    } catch {
      useMemory = true;
      resolve(null);
    }
  });
  return dbPromise;
}

export async function init(): Promise<void> {
  await openDb();
  await prune();
}

export function append(entry: LogEntry): void {
  if (useMemory) {
    pushMemory(entry);
    return;
  }
  openDb().then((db) => {
    if (!db) {
      pushMemory(entry);
      return;
    }
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).add(entry);
    } catch {
      // swallow
    }
  }).catch(() => { /* swallow */ });
}

function pushMemory(entry: LogEntry): void {
  memoryFallback.push(entry);
  if (memoryFallback.length > MAX_ENTRIES) memoryFallback.shift();
}

export async function getAllSince(ts: number): Promise<LogEntry[]> {
  const db = await openDb();
  if (!db) return memoryFallback.filter((e) => e.ts >= ts);
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).index('ts').getAll(IDBKeyRange.lowerBound(ts));
      req.onsuccess = () => resolve(req.result as LogEntry[]);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

export async function prune(): Promise<void> {
  const cutoff = Date.now() - RETENTION_MS;
  const db = await openDb();
  if (!db) {
    for (let i = memoryFallback.length - 1; i >= 0; i--) {
      if (memoryFallback[i].ts < cutoff) memoryFallback.splice(i, 1);
    }
    while (memoryFallback.length > MAX_ENTRIES) memoryFallback.shift();
    return;
  }

  // 1) drop entries older than the retention window
  await runTx(db, (store) => {
    const req = store.index('ts').openCursor(IDBKeyRange.upperBound(cutoff, true));
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  });

  // 2) enforce the hard cap, deleting oldest (lowest autoIncrement key) first
  await runTx(db, (store) => {
    const countReq = store.count();
    countReq.onsuccess = () => {
      let excess = countReq.result - MAX_ENTRIES;
      if (excess <= 0) return;
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor && excess > 0) {
          cursor.delete();
          excess--;
          cursor.continue();
        }
      };
    };
  });
}

function runTx(db: IDBDatabase, work: (store: IDBObjectStore) => void): Promise<void> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}
